import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from "@Src/app/hooks";
import { Dropdown } from 'primereact/dropdown';
import { DashHeaderIndex } from "@Src/features/Dashboard/Dash-header/Dash-header";
import { DashboardSidebarIndex } from "@Src/features/Dashboard/Sidebar/Index";
import './Analytics.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSubjectList } from '@Src/features/UserProfile/Services/UserProfile.sevice';
import { APIStatus } from '@Src/services/baseInterfaces';
import { Skeleton } from 'primereact/skeleton';
import { getTopicAnalysis } from './services/analytics.service';
import { AppRouteURL, TestSereisRouteURL } from '@Constants/app-route-url.constant';
import { useAnalyticsAccess } from '@Src/hooks/useAnalyticsAccess';
import { isInstituteStudent } from '@Src/utils/studentTypeUtils';
import { Trans, useTranslation } from "react-i18next";

export const AnalyticsIndex = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [selectedSubject, setSelectedSubject] = useState<any>(null);
    const [analysisData, setAnalysisData] = useState<any>(null);
    const [subjects, setSubjects] = useState<any[]>([]);
    const [loadingSubjects, setLoadingSubjects] = useState(false);
    const { isCheckingAccess, hasAnalyticsAccess } = useAnalyticsAccess();
    const userState = useAppSelector((state: any) => state.login);
    const isInstitute: boolean = isInstituteStudent(userState);

    const navigate = useNavigate();
    const location = useLocation();
    const userTestId = location?.state?.userTestId;
    const testName = location?.state?.testName;
    const coursePreferences = useAppSelector((state: any) => state.coursePreferences);


    const { t } = useTranslation("common");

    const analysisName =
        (userTestId && testName)
            ? t("topicwise_analysis_of", { testName })
            : t("overall_topicwise_analysis");

    const getTopicAnalysisData = async () => {
        try {
            const params: any = {};
            if (userTestId) params.userTestId = userTestId;
            if (selectedSubject?.Id && !userTestId) {
                const classId = coursePreferences?.classDetails[0]?.Id;
                const boardId = coursePreferences?.boardDetails[0]?.Id;
                params.subjectId = selectedSubject.Id;
                params.classId = classId;
                params.boardId = boardId;
            }

            const response = await getTopicAnalysis(params);
            if (response.ResponseType === "SUCCESS" && response.data.length > 0) {
                setAnalysisData(response.data);
            } else {
                setAnalysisData([]);
            }

        } catch (error) {
            console.log(error);
            setAnalysisData([]);
        }
    }

    useEffect(() => {
        // fetchSubjects();
        const subjectList = coursePreferences?.subjectDetails ? coursePreferences.subjectDetails.map((s: any) => ({ Id: s.Id, Name: s.Name })) : []
        setSubjects(subjectList)
        if (subjectList.length > 0) {
            setSelectedSubject(subjectList[0]);
        }
    }, [coursePreferences, userTestId]);

    useEffect(() => {
        setAnalysisData(null);
        if (selectedSubject) {
            getTopicAnalysisData();
        }

    }, [userTestId, selectedSubject]);


    const getPerformanceTier = (accuracy: number) => {
        if (accuracy < 30) return 'critical';
        if (30 <= accuracy && accuracy <= 50) return 'improving';
        if (50 < accuracy && accuracy <= 75) return 'developing';
        if (accuracy > 75) return 'mastery';
    };

    const topics = analysisData?.length > 0 ? analysisData.map((t: any) => ({
        ...t,
        tier: getPerformanceTier(t.WeightedAccuracy)
    })) : [];


    const groupedTopics = {
        priority: topics.filter((t: any) => t.tier === 'critical').sort((a: any, b: any) => a.WeightedAccuracy - b.WeightedAccuracy),
        improving: topics.filter((t: any) => t.tier === 'improving').sort((a: any, b: any) => a.WeightedAccuracy - b.WeightedAccuracy),
        progress: topics.filter((t: any) => t.tier === 'developing'),
        completed: topics.filter((t: any) => t.tier === 'mastery')
    };


    const TopicCard = ({ topic }: { topic: typeof topics[0] }) => {
        const getCardStyles = () => {


            switch (topic.tier) {
                case 'critical':
                    return 'topic-result-card theme-poor';
                case 'improving':
                    return 'topic-result-card theme-improving';
                case 'developing':
                    return 'topic-result-card theme-medium';
                case 'mastery':
                    return 'topic-result-card theme-good';
                default:
                    return 'bg-gray-50 border-gray-200';
            }
        };

        const getButtonStyles = () => {

            switch (topic.tier) {
                case 'critical':
                    return 'ia-btn ia-btn-primary learn-action-btn';
                case 'improving':
                    return 'ia-btn ia-btn-primary learn-action-btn';
                case 'developing':
                    return 'ia-btn ia-btn-primary learn-action-btn';
                case 'mastery':
                    return 'ia-btn ia-btn-primary learn-action-btn';
                default:
                    return 'bg-gray-500 hover:bg-gray-600';
            }
        };
        const getReviseButtonStyles = () => {

            switch (topic.tier) {
                case 'critical':
                    return 'ia-btn ia-btn-outline-primary tw-bg-white';
                case 'improving':
                    return 'ia-btn ia-btn-outline-primary tw-bg-white';
                case 'developing':
                    return 'ia-btn ia-btn-outline-primary tw-bg-white';
                case 'mastery':
                    return 'ia-btn ia-btn-outline-primary tw-bg-white';
                default:
                    return 'bg-gray-500 hover:bg-gray-600';
            }
        };
        const getButtonText = () => {
            switch (topic.tier) {
                case 'critical':
                    return 'Fix Now';
                case 'improving':
                    return 'Practice More';
                case 'developing':
                    return 'Improve Accuracy';
                case 'mastery':
                    return 'Master This';
                default:
                    return 'Fix Now';
            }
        }

        return (
            <div className={`rounded-lg py-3 px-4 border ${getCardStyles()} shadow-sm hover:shadow-md transition-all duration-200`}>
                <div className="md:tw-flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className=" tw-flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 tw-line-clamp-1" title={topic.TopicName}>{topic.TopicName}</h3>
                        <p className="text-xs font-semibold text-gray-700 mb-2  ">{t("chapter")}: {topic.ChapterName}
                            {/* <span className='text-gray-400 px-2'>|</span> */}
                        </p>
                        <div className="text-sm font-semibold text-gray-600 lg:tw-hidden">
                            {t("difficulty_based_accuracy")}: <span className="font-semibold text-gray-800">{topic.WeightedAccuracy}%</span>
                        </div>

                        <div className='tw-gap-2 tw-flex mt-4'>
                            <button className={`px-2 py-1 rounded-lg font-semibold text-white ${getButtonStyles()} transition-colors duration-200`} onClick={() => {
                                const board = coursePreferences?.boardDetails?.[0];
                                const classDetail = coursePreferences?.classDetails?.[0];
                                const subName = topic.SubjectName || selectedSubject?.Name || (subjects.find((s: any) => s.Id === topic.SubjectId)?.Name);
                                navigate(`${TestSereisRouteURL.TEST_SERIES}`, {
                                    state: {
                                        boardId: board?.Id,
                                        boardName: board?.Name,
                                        classId: classDetail?.Id,
                                        className: classDetail?.Name,
                                        subjectId: topic.SubjectId,
                                        subjectName: subName,
                                        chapterId: topic.ChapterId,
                                        chapterName: topic.ChapterName,
                                        topicId: topic.TopicId,
                                        topicName: topic.TopicName,
                                        topicPriority: 1,
                                        difficultyBasedAccuracy: topic.WeightedAccuracy
                                    }
                                })
                            }} >
                                {getButtonText()}
                            </button>
                            <button className={`px-2 py-1  ${getReviseButtonStyles()} transition-colors duration-200`} onClick={() => {
                                const board = coursePreferences?.boardDetails?.[0];
                                const classDetail = coursePreferences?.classDetails?.[0];
                                const subName = topic.SubjectName || selectedSubject?.Name || (subjects.find((s: any) => s.Id === topic.SubjectId)?.Name);
                                navigate(`${AppRouteURL.DASHBOARD}${AppRouteURL.MIND_MAP}`, {
                                    state: {
                                        boardId: board?.Id,
                                        boardName: board?.Name,
                                        classId: classDetail?.Id,
                                        className: classDetail?.Name,
                                        subjectId: topic.SubjectId,
                                        subjectName: subName,
                                        chapterId: topic.ChapterId,
                                        chapterName: topic.ChapterName,
                                        topicId: topic.TopicId,
                                        topicName: topic.TopicName,
                                        // topicPriority: 1
                                        returnFromResulPage: 1,
                                    }
                                })
                            }} >
                                Quick Revise
                            </button>
                        </div>
                    </div>
                    <div className='tw-hidden lg:tw-flex tw-ms-auto tw-px-4 tw-py-2 tw-rounded-lg tw-flex-col tw-items-center tw-justify-center tw-shadow' style={{ backgroundColor: 'rgba(255,255,255,0.4)' }}>
                        <div className="text-sm font-semibold text-gray-600 tw-text-center">
                            {t("difficulty_based_accuracy")}: <br /> <span className="font-semibold tw-text-3xl text-gray-800">{topic.WeightedAccuracy}%</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const TopicSkeletonCard = () => (
        <div className="topic-result-card tw-opacity-60" style={{ borderLeftColor: '#e0e0e0' }}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="tw-flex-1">
                    <Skeleton width="60%" height="1.5rem" className="mb-2" />
                    <Skeleton width="40%" height="1rem" className="mb-2" />
                    <Skeleton width="30%" height="1rem" />
                </div>
                <div className='tw-ms-auto'>
                    <Skeleton width="80px" height="40px" borderRadius="8px" />
                </div>
            </div>
        </div>
    );

    const SectionGroup = ({
        title,
        topics
    }: {
        title: string;
        topics: typeof groupedTopics.priority;
    }) => {
        return (
            <div className="results-list">
                <div className="mt-2">
                    <div className='tw-flex tw-items-center tw-gap-3 mb-2'>
                        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                        <p className='p-0 tw-rounded tw-border tw-shadow-lg tw-bg-gray-100 tw-text-gray-700 px-2 py-1 tw-text-sm tw-text-semibold'>{topics.length}</p>
                    </div>
                </div>

                <div className="space-y-4 tw-grid md:tw-grid-cols-2 gap-4">
                    {topics.map((topic: any, index: number) => (
                        <TopicCard key={topic.id || index} topic={topic} />
                    ))}
                </div>
            </div>
        );
    }



    return (
        <>

            <div className="dashboard-container">

                <div className="dashboard-content-page" onClick={() => isSidebarOpen && setIsSidebarOpen(false)}>
                    <div className="analytics-page-container">
                        <div className="analytics-inner">
                            <button className="back-link-btn" onClick={() => window.history.back()}>
                                <i className="pi pi-chevron-left" /> Back
                            </button>

                            {/* <div className={`summary-banner theme-${activeFilter}`}>
                                <div className="banner-content">
                                    Showing {filteredChapters.length} {filterLabels[activeFilter]}
                                </div>
                            </div> */}

                            <div className="page-section-header">
                                <div className="tw-flex tw-flex-col tw-flex-1">
                                    <div className='mb-3'>
                                        {analysisData === null ? (
                                            <>
                                                <Skeleton width="15rem" height="2rem" className="mb-2" />
                                                <Skeleton width="20rem" height="1.5rem" />
                                            </>
                                        ) : (
                                            <>
                                                <h2 className="section-title">{(userTestId && testName) ? t("topicwise_analysis_of", { testName }) : t("overall_topicwise_analysis")}</h2>
                                                <h3 className="section-subtitle tw-font-semibold">{t("analyze_your_performance_by_topics")}</h3>
                                            </>
                                        )}
                                    </div>

                                    <div className='lg:tw-flex tw-justify-between tw-items-center'>
                                        <div className="performance-legend md:tw-flex gap-3 mt-1">
                                            {
                                                isCheckingAccess ?
                                                    <>
                                                        <Skeleton width="12rem" height="3.5rem" borderRadius="0.75rem" />
                                                        <Skeleton width="12rem" height="3.5rem" borderRadius="0.75rem" />
                                                        <Skeleton width="12rem" height="3.5rem" borderRadius="0.75rem" />
                                                    </>
                                                    :
                                                    hasAnalyticsAccess &&
                                                    <>
                                                        {analysisData === null ? (
                                                            <>
                                                                <Skeleton width="12rem" height="3.5rem" borderRadius="0.75rem" />
                                                                <Skeleton width="12rem" height="3.5rem" borderRadius="0.75rem" />
                                                                <Skeleton width="12rem" height="3.5rem" borderRadius="0.75rem" />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="legend-item tw-flex tw-flex-col tw-bg-red-100 tw-px-4 tw-py-2 tw-rounded-lg my-2 md:my-0">
                                                                    <span className='tw-font-semibold'><span className="dot dot-poor"></span>{t("need_more_practice")}</span>
                                                                    <small className='ml-3 tw-font-semibold'>{`${t("accuracy")} < 30%`}</small>
                                                                </div>
                                                                <div className="legend-item tw-flex tw-flex-col tw-bg-orange-100 tw-px-4 tw-py-2 tw-rounded-lg my-2 md:my-0">
                                                                    <span className='tw-font-semibold'><span className="dot dot-improving"></span> {t("improving")}</span>
                                                                    <small className='ml-3 tw-font-semibold'>{`${t("accuracy")} 30% - 50%`}</small>
                                                                </div>
                                                                <div className="legend-item tw-flex tw-flex-col tw-bg-yellow-100 tw-px-4 tw-py-2 tw-rounded-lg my-2 md:my-0">
                                                                    <span className='tw-font-semibold'><span className="dot dot-medium"></span> {t("developing")}</span>
                                                                    <small className='ml-3 tw-font-semibold'>{`${t("accuracy")} 51% - 75%`}</small>
                                                                </div>
                                                                <div className="legend-item tw-flex tw-flex-col tw-bg-green-100 tw-px-4 tw-py-2 tw-rounded-lg my-2 md:my-0">
                                                                    <span className='tw-font-semibold'><span className="dot dot-good"></span> {t("mastery")}</span>
                                                                    <small className='ml-3 tw-font-semibold'>{`${t("accuracy")} > 75%`}</small>
                                                                </div>
                                                            </>
                                                        )}
                                                    </>

                                            }

                                        </div>
                                        {isCheckingAccess ? (
                                            <div className='searchBySubject tw-flex tw-items-center tw-gap-3 mt-4 lg:mt-0 ml-auto'>

                                                <div className="tw-flex tw-items-center tw-gap-3">
                                                    <Skeleton width="8rem" height="1.2rem" />
                                                    <Skeleton width="180px" height="42px" borderRadius="0.75rem" />
                                                </div>
                                            </div>
                                        )
                                            :
                                            hasAnalyticsAccess &&
                                            <>
                                                {
                                                    !userTestId &&
                                                    <div className='searchBySubject tw-flex tw-items-center tw-gap-3 mt-4 lg:mt-0 ml-auto'>
                                                        {loadingSubjects ? (
                                                            <div className="tw-flex tw-items-center tw-gap-3">
                                                                <Skeleton width="8rem" height="1.2rem" />
                                                                <Skeleton width="180px" height="42px" borderRadius="0.75rem" />
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <span className='tw-font-semibold'>{t("search_by_subject")}</span>
                                                                <Dropdown
                                                                    value={selectedSubject}
                                                                    onChange={(e) => setSelectedSubject(e.value)}
                                                                    options={subjects}
                                                                    optionLabel="Name"
                                                                    placeholder="Select Subject"
                                                                    className="analytics-filter-dropdown"
                                                                    appendTo="self"
                                                                />
                                                            </>
                                                        )}

                                                    </div>
                                                }

                                            </>

                                        }
                                    </div>
                                </div>
                            </div>

                            <div className="analytics-flex-layout tw-flex-col gap-4">
                                {
                                    isCheckingAccess ?
                                        <>
                                            {[1, 2, 3].map((i) => (
                                                <TopicSkeletonCard key={i} />
                                            ))}
                                        </>
                                        :
                                        hasAnalyticsAccess ?
                                            <>
                                                {analysisData === null ? (
                                                    <>
                                                        {[1, 2, 3].map((i) => (
                                                            <TopicSkeletonCard key={i} />
                                                        ))}
                                                    </>
                                                ) : topics.length > 0 ? (
                                                    <>
                                                        {groupedTopics.priority.length > 0 && (
                                                            <SectionGroup
                                                                title={t("priority_focus")}
                                                                topics={groupedTopics.priority}
                                                            />
                                                        )}

                                                        {groupedTopics.improving.length > 0 && (
                                                            <SectionGroup
                                                                title={t("improving")}
                                                                topics={groupedTopics.improving}
                                                            />
                                                        )}

                                                        {groupedTopics.progress.length > 0 && (
                                                            <SectionGroup
                                                                title={t("developing")}
                                                                topics={groupedTopics.progress}
                                                            />
                                                        )}

                                                        {groupedTopics.completed.length > 0 && (
                                                            <SectionGroup
                                                                title={t("mastery")}
                                                                topics={groupedTopics.completed}
                                                            />
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="empty-state-card">
                                                        {t("no_analysis_data_found")}
                                                    </div>
                                                )}
                                            </>
                                            :
                                            <>
                                                <div className="tw-p-10 tw-text-center tw-text-lg tw-font-medium tw-text-gray-600">
                                                    {isInstitute ? (
                                                        <Trans
                                                            i18nKey="no_permission_review_institute"
                                                            values={{ analysisName }}
                                                            components={{
                                                                highlight: (
                                                                    <b className="tw-text-purple-700" />
                                                                ),
                                                            }}
                                                        />
                                                    ) : (
                                                        <Trans
                                                            i18nKey="no_permission_review_student"
                                                            values={{ analysisName }}
                                                            components={{
                                                                highlight: (
                                                                    <b className="tw-text-purple-700" />
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </>

                                }


                            </div>
                        </div>
                    </div>
                </div>

                {isSidebarOpen && (
                    <div className="sidebar-backdrop" onClick={() => setIsSidebarOpen(false)} />
                )}
            </div>
        </>
        //    </div>
        //     </div>
        // </div>
        //    </div>
        //     </div>
        // </div>
    );
};
